/**
 * Module dependencies.
 */

import { FieldValues, UseFormReturn } from 'react-hook-form';
import React, { useCallback, useMemo, useState } from 'react';
import { CrudFormRender, CrudFormRenderProps } from '../../crud-form';
import { ListItemFormType, NestedFormList } from '../../form-types';
import { useTranslation } from 'react-i18next';
import { Button } from 'app/components/atoms/button/button';
import { range } from 'lodash';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from 'app/components/ui/alert-dialog';
import { Plus, Trash2 } from 'lucide-react';

/**
 * `Props` type.
 */

type Props = {
  formType: 'add' | 'edit';
  name: string;
  item: ListItemFormType;
  form: UseFormReturn<FieldValues, any>;
};

/**
 * `NestedFormProps` type.
 */

type NestedFormProps = Omit<CrudFormRenderProps, 'formFields'> & {
  index: number;
  name: string;
  formFields: NestedFormList;
};

/**
 * `NestedForm` component.
 */

function NestedForm(props: NestedFormProps): JSX.Element {
  const { form, name, index, formFields, formType } = props;
  const nestedFormFields = useMemo(() => {
    return formFields(name, index).map(item => ({
      ...item,
      name: `${props.name}.${props.index}.${item.name}`
    }));
  }, [formFields, index, name, props.index, props.name]);

  return <CrudFormRender form={form} formFields={nestedFormFields} formType={formType} />;
}

/**
 * Export `ListItemForm` component.
 */

export function ListItemForm(props: Props): JSX.Element | null {
  const [translate] = useTranslation();
  const { form, formType, item } = props;
  const { formFields, name, defaultItemsLength } = item;
  const [items, setItems] = useState(() => {
    const values = form.getValues();

    if (values?.[item.name]?.length > 0) {
      return range(values?.[item.name].length);
    }

    const list = range(defaultItemsLength);

    for (const index of list) {
      if (item.normalizeValues) {
        form.setValue(`${item.name}.${index}`, item.normalizeValues(form.getValues()));
      }
    }

    return list;
  });

  const removeItem = useCallback(
    (index: number) => {
      form.setValue(`${item.name}.${index}`, undefined);

      setItems(items => {
        return items.filter(item => item !== index);
      });
    },
    [form, item.name]
  );

  return (
    <>
      <div className={'flex flex-col gap-4'}>
        {items.map((index, position) => (
          <div
            className={
              'mb-0 min-w-0 rounded-[10px] border border-[#e2e2e2] bg-white px-5 pb-5 pt-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
            }
            key={index}
          >
            <div className={'mb-[14px] flex items-center justify-between border-b border-[#ededed] pb-[10px]'}>
              <span className={'text-[13px] font-semibold uppercase tracking-[0.4px] text-[#5e5e5e]'}>
                {`Divisão ${position + 1}`}
              </span>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    danger
                    icon={<Trash2 className={'h-4 w-4'} />}
                    onClick={(event: any) => event.stopPropagation()}
                    size={'small'}
                  />
                </AlertDialogTrigger>

                <AlertDialogContent onClick={(event: any) => event.stopPropagation()}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{translate('removeAction')}</AlertDialogTitle>
                  </AlertDialogHeader>

                  <AlertDialogFooter>
                    <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

                    <AlertDialogAction
                      className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
                      onClick={(event: any) => {
                        event.stopPropagation();
                        removeItem(index);
                      }}
                    >
                      {translate('common.actions.remove')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div
              className={
                'grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] items-start gap-x-4 gap-y-1 [&>*]:min-w-0'
              }
            >
              <NestedForm form={form} formFields={formFields} formType={formType} index={index} name={name} />
            </div>
          </div>
        ))}
      </div>

      <Button
        icon={<Plus className={'h-4 w-4'} />}
        onClick={() => {
          setItems(items => {
            const lastItem = (items.length > 0 ? items[items.length - 1] : -1) + 1;

            if (item.normalizeValues) {
              form.setValue(`${item.name}.${lastItem}`, item.normalizeValues(form.getValues()));
            }

            return [...items, lastItem];
          });
        }}
      >
        {translate('common.actions.addNewItem')}
      </Button>
    </>
  );
}
